import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, MapPin, DollarSign, ShieldCheck, MessageSquare,
  User, Star, ThumbsUp, X, CheckCircle2, Send
} from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useDocumentTitle(data?.request?.title || "Request");

  const load = async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setData(data);
    } catch (err) {
      toast.error("Could not load request");
    }
  };
  useEffect(() => { load(); }, [id]);

  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposal, setProposal] = useState({
    intro: "", scope: "", price: 0, pricing_type: "fixed", terms: "",
    inclusions: [""], exclusions: [""], estimated_hours: 0,
  });

  const submitProposal = async () => {
    try {
      const payload = {
        intro: proposal.intro,
        scope: proposal.scope,
        price: Number(proposal.price),
        pricing_type: proposal.pricing_type,
        estimated_hours: proposal.pricing_type === "hourly" ? Number(proposal.estimated_hours) : null,
        inclusions: proposal.inclusions.filter(Boolean),
        exclusions: proposal.exclusions.filter(Boolean),
        terms: proposal.terms,
      };
      await api.post(`/requests/${id}/proposals`, payload);
      toast.success("Proposal submitted");
      setProposalOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Cannot submit proposal");
    }
  };

  const decide = async (proposalId, action) => {
    try {
      const { data: r } = await api.post(`/requests/${id}/proposals/${proposalId}/${action}`);
      toast.success(`Proposal ${action}`);
      if (action === "accept") {
        if (r.booking_id) navigate(`/dashboard/bookings/${r.booking_id}`);
        else if (r.order_id) navigate(`/dashboard/orders/${r.order_id}`);
      } else {
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Cannot decide");
    }
  };

  if (!data) return <PublicLayout><div className="mx-auto max-w-3xl px-6 py-24 text-[#252A34]/60">Loading…</div></PublicLayout>;

  const r = data.request;
  const isOwner = user?.id === r.customer_id;
  const alreadyProposed = (data.proposals || []).some((p) => p.provider_user_id === user?.id && ["submitted", "shortlisted"].includes(p.status));

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-10">
        <button onClick={() => navigate(-1)} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Project request · {r.kind}</div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]" data-testid="request-title">{r.title}</h1>
              </div>
              <StatusPill status={r.status} size="lg" />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-[#252A34]/70 mb-6">
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {r.urgency?.replace(/_/g, " ")}</span>
              {r.address_snapshot?.city && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {r.address_snapshot.city}, {r.address_snapshot.state}</span>}
              <span className="inline-flex items-center gap-1.5"><DollarSign className="h-4 w-4" /> {
                r.budget_type === "hourly" ? `$${r.budget_hourly}/hr`
                : r.budget_type === "open" ? "Open budget"
                : `$${r.budget_min ?? 0} - $${r.budget_max ?? "?"}`
              }</span>
              <span>· {r.proposal_count || 0} proposals</span>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="font-display font-semibold text-[#071b22] mb-2">Project details</div>
              <p className="whitespace-pre-line text-[#252A34]/85">{r.description}</p>
              {(r.require_insurance || r.require_background_check) && (
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  {r.require_insurance && <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1">Insurance required</span>}
                  {r.require_background_check && <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1">Background-check required</span>}
                  {r.remote_ok && <span className="rounded-full bg-slate-50 border border-slate-200 text-[#252A34] px-2.5 py-1">Remote OK</span>}
                </div>
              )}
            </div>

            {/* Proposals */}
            {(data.proposals?.length > 0 || isOwner) && (
              <div className="mt-8">
                <div className="font-display text-xl font-semibold text-[#071b22] mb-3">{isOwner ? "Proposals" : "My proposal"}</div>
                <div className="space-y-3" data-testid="proposals-list">
                  {(data.proposals || []).map((p) => (
                    <ProposalCard key={p.id} p={p} isOwner={isOwner} onDecide={decide} />
                  ))}
                  {isOwner && (data.proposals || []).length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-[#252A34]/70">
                      No proposals yet — publish widely to attract more!
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-4">
              {!isOwner && user && !alreadyProposed && (
                <button
                  onClick={() => setProposalOpen(true)}
                  className="w-full rounded-full bg-[#165a71] text-white px-5 py-3 font-medium hover:bg-[#11485a]"
                  data-testid="open-proposal-btn"
                >Submit a proposal</button>
              )}
              {!isOwner && alreadyProposed && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                  ✅ You already submitted a proposal on this request.
                </div>
              )}
              {!user && (
                <Link to="/login" className="block w-full text-center rounded-full bg-[#165a71] text-white px-5 py-3 font-medium">Log in to submit a proposal</Link>
              )}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm">
                <div className="font-display font-semibold text-[#071b22] mb-2">Posted</div>
                <div className="text-[#252A34]/70">{r.published_at ? new Date(r.published_at).toLocaleString() : "—"}</div>
                {r.expires_at && <div className="text-xs text-[#252A34]/50 mt-1">Expires {new Date(r.expires_at).toLocaleDateString()}</div>}
              </div>
            </div>
          </div>
        </div>

        {proposalOpen && (
          <ProposalModal
            value={proposal}
            onChange={setProposal}
            onClose={() => setProposalOpen(false)}
            onSubmit={submitProposal}
          />
        )}
      </div>
    </PublicLayout>
  );
}

function ProposalCard({ p, isOwner, onDecide }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`proposal-${p.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl overflow-hidden bg-[#165a71]/10 flex items-center justify-center text-[#165a71]">
            {p.provider_snapshot?.profile_image ? <img src={p.provider_snapshot.profile_image} alt="" className="h-full w-full object-cover" /> : <User className="h-4 w-4" />}
          </div>
          <div>
            <div className="font-display font-semibold text-[#071b22]">{p.provider_snapshot?.display_name || "Provider"}</div>
            <div className="text-xs text-[#252A34]/60 flex items-center gap-2">
              <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-amber-500 fill-amber-500" /> {(p.provider_snapshot?.rating_avg || 0).toFixed(1)} ({p.provider_snapshot?.rating_count || 0})</span>
              <span>· {p.provider_snapshot?.completed_jobs || 0} jobs</span>
            </div>
          </div>
        </div>
        <StatusPill status={p.status} />
      </div>
      <div className="text-sm text-[#252A34] whitespace-pre-line">{p.intro}</div>
      {p.scope && <div className="mt-2 text-xs text-[#252A34]/70">{p.scope}</div>}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <span className="font-display text-lg font-semibold text-[#071b22]">${p.price?.toFixed(2)}</span>
        <span className="text-xs text-[#252A34]/60">{p.pricing_type}{p.estimated_hours ? ` · ~${p.estimated_hours}h` : ""}</span>
        {p.milestones?.length > 0 && <span className="text-xs text-emerald-700">{p.milestones.length} milestones</span>}
      </div>
      {isOwner && ["submitted", "shortlisted"].includes(p.status) && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button className="rounded-full bg-emerald-600 text-white px-4 py-2 text-xs font-medium" onClick={() => onDecide(p.id, "accept")} data-testid={`accept-${p.id}`}><CheckCircle2 className="h-4 w-4 inline mr-1" /> Accept</button>
          {p.status !== "shortlisted" && <button className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium" onClick={() => onDecide(p.id, "shortlist")}><ThumbsUp className="h-4 w-4 inline mr-1" /> Shortlist</button>}
          <button className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-red-600 hover:bg-red-50" onClick={() => onDecide(p.id, "decline")}><X className="h-4 w-4 inline mr-1" /> Decline</button>
        </div>
      )}
    </div>
  );
}

function ProposalModal({ value, onChange, onClose, onSubmit }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="proposal-modal" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display text-xl font-semibold text-[#071b22]">Submit proposal</div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <F label="Intro"><textarea rows={3} value={value.intro} onChange={(e) => set("intro", e.target.value)} className="p-input" data-testid="proposal-intro" /></F>
          <F label="Scope"><textarea rows={4} value={value.scope} onChange={(e) => set("scope", e.target.value)} className="p-input" data-testid="proposal-scope" /></F>
          <div className="grid gap-3 sm:grid-cols-3">
            <F label="Type">
              <select value={value.pricing_type} onChange={(e) => set("pricing_type", e.target.value)} className="p-input" data-testid="proposal-type">
                <option value="fixed">Fixed</option>
                <option value="hourly">Hourly</option>
                <option value="milestone">Milestone</option>
              </select>
            </F>
            <F label={value.pricing_type === "hourly" ? "Hourly rate" : "Total price"}><input type="number" value={value.price} onChange={(e) => set("price", e.target.value)} className="p-input" data-testid="proposal-price" /></F>
            {value.pricing_type === "hourly" && <F label="Est. hours"><input type="number" value={value.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} className="p-input" /></F>}
          </div>
          <F label="Terms (optional)"><textarea rows={2} value={value.terms} onChange={(e) => set("terms", e.target.value)} className="p-input" placeholder="Deposit, cancellation, timelines…" /></F>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
          <button onClick={onSubmit} disabled={!value.intro || !value.scope || !value.price} className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium disabled:opacity-60" data-testid="proposal-submit">
            <Send className="h-4 w-4 inline mr-1" /> Submit
          </button>
        </div>
        <style>{`
          .p-input { width: 100%; border-radius: 0.75rem; border: 1px solid #e2e8f0; background: white; padding: 0.6rem 0.75rem; font-size: 0.875rem; outline: none; }
          .p-input:focus { border-color: #165a71; box-shadow: 0 0 0 3px rgba(22, 90, 113, 0.12); }
        `}</style>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-medium text-[#252A34]/70 mb-1">{label}</div>
    {children}
  </label>
);
